import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { formatPower } from '../../../../public/formatPower';

const getChartDensity = (labelCount) => {
  if (labelCount > 80) {
    return { maxBarThickness: 4, maxTicksLimit: 10, fontSize: 9, rotation: 0, categoryPercentage: 0.95, barPercentage: 0.9 };
  }
  if (labelCount > 40) {
    return { maxBarThickness: 6, maxTicksLimit: 14, fontSize: 10, rotation: 0, categoryPercentage: 0.9, barPercentage: 0.85 };
  }
  return { maxBarThickness: 16, maxTicksLimit: 24, fontSize: 12, rotation: 45, categoryPercentage: 0.8, barPercentage: 0.9 };
};

const HourlyChartComponent = ({ displayedData, viewMode, tooltipBySlot }) => {
  const labelCount = displayedData?.labels?.length ?? 0;
  const hasData = labelCount > 0;

  const density = useMemo(() => getChartDensity(labelCount), [labelCount]);

  const chartData = useMemo(() => {
    if (!hasData) return null;
    return {
      labels: displayedData.labels,
      datasets: displayedData.datasets.map((dataset) => ({
        ...dataset,
        maxBarThickness: density.maxBarThickness,
        categoryPercentage: density.categoryPercentage,
        barPercentage: density.barPercentage,
      })),
    };
  }, [displayedData, density, hasData]);

  const maxVal = useMemo(() => {
    if (!hasData) return 0;
    const allValues = displayedData.datasets.flatMap((dataset) => dataset.data);
    return Math.max(...allValues.map((val) => val / 1000), 0);
  }, [displayedData, hasData]);

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: density.rotation === 0 ? 4 : 8,
        },
      },
      plugins: {
        legend: {
          display: viewMode === 'individual',
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(tooltipItem) {
              const value = tooltipItem.raw;
              const label = tooltipItem.dataset.label || '발전량';
              if (value == null) {
                return `${label}: N/A`;
              }
              const formatted = formatPower(value, 'Add');
              return `${label}: ${formatted.value} ${formatted.unit}`;
            },
            afterBody(tooltipItems) {
              if (tooltipItems.length === 0) {
                return [];
              }

              const index = tooltipItems[0].dataIndex;
              const item = tooltipBySlot?.[index];
              if (!item) return ['데이터 없음'];

              const lines = [`측정시각: ${item.created_at || 'N/A'}`];

              if (viewMode === 'total' && item.Grid_Power != null) {
                lines.unshift(`인버터출력: ${(item.Grid_Power / 1000).toFixed(3)} kW`);
              }

              return lines;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: density.rotation,
            minRotation: density.rotation,
            autoSkip: true,
            maxTicksLimit: density.maxTicksLimit,
            font: { size: density.fontSize },
          },
          grid: {
            display: labelCount <= 40,
          },
        },
        y: {
          title: {
            display: true,
            text: '1시간 발전량 (kWh)',
          },
          beginAtZero: true,
          ticks: {
            callback(value) {
              return (Number(value) / 1000).toFixed(1);
            },
            stepSize: maxVal <= 0 ? 1000 : maxVal <= 1 ? 100 : Math.ceil(maxVal) * 1000,
          },
        },
      },
    }),
    [density, labelCount, maxVal, tooltipBySlot, viewMode]
  );

  if (!hasData) {
    return <div className="gen-hourly-empty">조회 버튼을 눌러 시간별 발전량을 확인하세요.</div>;
  }

  return (
    <div className="hourly-chart-scroll">
      <Bar data={chartData} options={options} />
    </div>
  );
};

export default HourlyChartComponent;
